"use client";

import React, { useState } from 'react';
import {
  CheckCircle, XCircle, Clock, AlertCircle, ChevronDown, ChevronUp,
  BookOpen, Shield, TrendingUp, ClipboardList, Filter, RefreshCw
} from 'lucide-react';

interface RemediationTask {
  id: string;
  task: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  dueDate: string;
  assignee: string;
  done: boolean;
}

interface ComplianceSection {
  section: string;
  title: string;
  description: string;
  status: 'compliant' | 'non_compliant' | 'in_review' | 'pending';
  score: number;
  lastAudit: string;
  remediationTasks: RemediationTask[];
}

const complianceSections: ComplianceSection[] = [
  {
    section: '§4',
    title: 'Data Protection Measures',
    description: 'Organizations must implement technical and organizational measures to protect personal and sensitive data from unauthorized access, disclosure, or destruction.',
    status: 'compliant',
    score: 94,
    lastAudit: '2026-03-10',
    remediationTasks: [
      { id: 't1', task: 'Review data classification policy', priority: 'low', dueDate: '2026-04-15', assignee: 'Admin', done: true },
      { id: 't2', task: 'Update data retention schedule', priority: 'medium', dueDate: '2026-04-30', assignee: 'Analyst', done: false },
    ],
  },
  {
    section: '§7',
    title: 'Incident Reporting (24h)',
    description: 'All cybersecurity incidents must be reported to CIRT Bangladesh within 24 hours of detection. Critical incidents require immediate notification.',
    status: 'compliant',
    score: 88,
    lastAudit: '2026-03-12',
    remediationTasks: [
      { id: 't3', task: 'Automate CIRT notification workflow', priority: 'medium', dueDate: '2026-04-20', assignee: 'Admin', done: true },
      { id: 't4', task: 'Test incident escalation runbook', priority: 'low', dueDate: '2026-05-01', assignee: 'Analyst', done: false },
    ],
  },
  {
    section: '§11',
    title: 'Access Control & IAM',
    description: 'Implement role-based access control (RBAC), enforce least-privilege principles, and conduct quarterly access reviews for all critical systems.',
    status: 'in_review',
    score: 72,
    lastAudit: '2026-03-05',
    remediationTasks: [
      { id: 't5', task: 'Complete Q1 access review for all admin accounts', priority: 'high', dueDate: '2026-03-25', assignee: 'Admin', done: false },
      { id: 't6', task: 'Implement privileged access workstations (PAW)', priority: 'high', dueDate: '2026-04-10', assignee: 'Admin', done: false },
      { id: 't7', task: 'Enable just-in-time (JIT) access for production', priority: 'medium', dueDate: '2026-04-30', assignee: 'Analyst', done: false },
    ],
  },
  {
    section: '§14',
    title: 'Encryption Standards',
    description: 'All data at rest must use AES-256 encryption. Data in transit must use TLS 1.2 or higher. Cryptographic keys must be managed via a dedicated KMS.',
    status: 'compliant',
    score: 91,
    lastAudit: '2026-03-08',
    remediationTasks: [
      { id: 't8', task: 'Migrate legacy TLS 1.0 endpoints to TLS 1.3', priority: 'high', dueDate: '2026-03-30', assignee: 'Admin', done: true },
      { id: 't9', task: 'Rotate KMS master keys (annual)', priority: 'medium', dueDate: '2026-06-01', assignee: 'Admin', done: false },
    ],
  },
  {
    section: '§18',
    title: 'Audit Log Retention (1yr)',
    description: 'Security audit logs must be retained for a minimum of 1 year. Logs must be tamper-proof, time-stamped, and accessible for forensic investigation.',
    status: 'non_compliant',
    score: 41,
    lastAudit: '2026-02-28',
    remediationTasks: [
      { id: 't10', task: 'Configure log archival to cold storage (S3/GCS)', priority: 'critical', dueDate: '2026-03-20', assignee: 'Admin', done: false },
      { id: 't11', task: 'Enable WORM (write-once-read-many) log protection', priority: 'critical', dueDate: '2026-03-25', assignee: 'Admin', done: false },
      { id: 't12', task: 'Validate log integrity with hash chaining', priority: 'high', dueDate: '2026-04-05', assignee: 'Analyst', done: false },
      { id: 't13', task: 'Document log retention policy and get sign-off', priority: 'medium', dueDate: '2026-04-15', assignee: 'Admin', done: false },
    ],
  },
  {
    section: '§21',
    title: 'Vulnerability Assessment',
    description: 'Conduct quarterly vulnerability assessments and annual penetration tests. Critical vulnerabilities must be patched within 72 hours of discovery.',
    status: 'in_review',
    score: 63,
    lastAudit: '2026-03-01',
    remediationTasks: [
      { id: 't14', task: 'Schedule Q1 2026 penetration test with certified vendor', priority: 'high', dueDate: '2026-03-31', assignee: 'Admin', done: false },
      { id: 't15', task: 'Patch 14 open critical CVEs from last scan', priority: 'critical', dueDate: '2026-03-22', assignee: 'Analyst', done: false },
      { id: 't16', task: 'Integrate DAST into CI/CD pipeline', priority: 'medium', dueDate: '2026-04-30', assignee: 'Admin', done: false },
    ],
  },
  {
    section: '§25',
    title: 'Third-party Risk Management',
    description: 'All third-party vendors with access to organizational data must undergo security assessments. Contracts must include cybersecurity clauses per BTRC guidelines.',
    status: 'pending',
    score: 0,
    lastAudit: 'Not started',
    remediationTasks: [
      { id: 't17', task: 'Create vendor security assessment questionnaire', priority: 'high', dueDate: '2026-04-01', assignee: 'Admin', done: false },
      { id: 't18', task: 'Inventory all third-party integrations and APIs', priority: 'high', dueDate: '2026-04-01', assignee: 'Analyst', done: false },
      { id: 't19', task: 'Draft cybersecurity addendum for vendor contracts', priority: 'medium', dueDate: '2026-04-15', assignee: 'Admin', done: false },
      { id: 't20', task: 'Conduct initial risk assessment for top 5 vendors', priority: 'medium', dueDate: '2026-04-30', assignee: 'Analyst', done: false },
    ],
  },
  {
    section: '§29',
    title: 'Cyber Drill Exercises',
    description: 'Organizations must conduct bi-annual cybersecurity drill exercises simulating real-world attack scenarios. Results must be documented and submitted to CIRT.',
    status: 'non_compliant',
    score: 33,
    lastAudit: '2025-09-15',
    remediationTasks: [
      { id: 't21', task: 'Schedule H1 2026 tabletop exercise (ransomware scenario)', priority: 'critical', dueDate: '2026-03-31', assignee: 'Admin', done: false },
      { id: 't22', task: 'Develop incident response playbooks for top 5 scenarios', priority: 'high', dueDate: '2026-04-15', assignee: 'Analyst', done: false },
      { id: 't23', task: 'Submit H2 2025 drill report to CIRT Bangladesh', priority: 'critical', dueDate: '2026-03-20', assignee: 'Admin', done: false },
    ],
  },
];

const statusConfig = {
  compliant: { icon: CheckCircle, color: '#00E676', bg: 'bg-green-500/10', border: 'border-green-500/25', label: 'Compliant', textColor: 'text-green-400' },
  non_compliant: { icon: XCircle, color: '#FF3B3B', bg: 'bg-red-500/10', border: 'border-red-500/25', label: 'Non-Compliant', textColor: 'text-red-400' },
  in_review: { icon: Clock, color: '#FFB800', bg: 'bg-yellow-500/10', border: 'border-yellow-500/25', label: 'In Review', textColor: 'text-yellow-400' },
  pending: { icon: AlertCircle, color: '#6B7A99', bg: 'bg-white/5', border: 'border-cyber-border', label: 'Pending', textColor: 'text-cyber-muted' },
};

const priorityConfig = {
  critical: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  high: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  medium: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  low: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
};

type FilterStatus = 'all' | 'compliant' | 'non_compliant' | 'in_review' | 'pending';

export default function ComplianceChecklist() {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [taskStates, setTaskStates] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    complianceSections.forEach(s => s.remediationTasks.forEach(t => { init[t.id] = t.done; }));
    return init;
  });
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [lastRefresh] = useState(new Date().toLocaleTimeString());

  const toggleTask = (taskId: string) => {
    setTaskStates(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const toggleSection = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  // Compute stats
  const total = complianceSections.length;
  const compliantCount = complianceSections.filter(s => s.status === 'compliant').length;
  const nonCompliantCount = complianceSections.filter(s => s.status === 'non_compliant').length;
  const inReviewCount = complianceSections.filter(s => s.status === 'in_review').length;
  const pendingCount = complianceSections.filter(s => s.status === 'pending').length;
  const overallScore = Math.round(complianceSections.reduce((sum, s) => sum + s.score, 0) / total);

  const allTasks = complianceSections.flatMap(s => s.remediationTasks);
  const completedTasks = allTasks.filter(t => taskStates[t.id]).length;
  const criticalOpenTasks = allTasks.filter(t => t.priority === 'critical' && !taskStates[t.id]).length;

  const filteredSections = filterStatus === 'all'
    ? complianceSections
    : complianceSections.filter(s => s.status === filterStatus);

  const scoreColor = overallScore >= 80 ? '#00E676' : overallScore >= 60 ? '#FFB800' : '#FF3B3B';
  const scoreTextColor = overallScore >= 80 ? 'text-green-400' : overallScore >= 60 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <BookOpen size={16} className="text-cyber-blue" />
            BD Cyber Security Act 2023
          </h2>
          <p className="text-xs text-cyber-muted font-mono mt-0.5">Compliance checklist · Last refreshed {lastRefresh}</p>
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyber-blue/10 border border-cyber-blue/20 text-cyber-blue text-xs font-medium hover:bg-cyber-blue/20 transition-colors">
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {/* Progress Dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Overall Score */}
        <div className="col-span-2 lg:col-span-1 bg-[#0F1117] border border-cyber-border rounded-xl p-4 flex items-center gap-4">
          <div className="relative w-16 h-16 shrink-0">
            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="26" fill="none" stroke="#1E2435" strokeWidth="6" />
              <circle
                cx="32" cy="32" r="26" fill="none"
                stroke={scoreColor}
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 26}`}
                strokeDashoffset={`${2 * Math.PI * 26 * (1 - overallScore / 100)}`}
                strokeLinecap="round"
                className="transition-all duration-700"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-sm font-bold font-mono ${scoreTextColor}`}>{overallScore}%</span>
            </div>
          </div>
          <div>
            <p className="text-[10px] text-cyber-muted font-mono uppercase tracking-widest">Overall Score</p>
            <p className={`text-lg font-bold font-mono ${scoreTextColor}`}>
              {overallScore >= 80 ? 'Good' : overallScore >= 60 ? 'Fair' : 'Poor'}
            </p>
            <p className="text-[10px] text-cyber-muted font-mono">{compliantCount}/{total} sections</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="bg-[#0F1117] border border-green-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={14} className="text-green-400" />
            <span className="text-[10px] text-cyber-muted font-mono uppercase tracking-widest">Compliant</span>
          </div>
          <p className="text-2xl font-bold font-mono text-green-400">{compliantCount}</p>
          <p className="text-[10px] text-cyber-muted font-mono">of {total} sections</p>
        </div>

        <div className="bg-[#0F1117] border border-red-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <XCircle size={14} className="text-red-400" />
            <span className="text-[10px] text-cyber-muted font-mono uppercase tracking-widest">Non-Compliant</span>
          </div>
          <p className="text-2xl font-bold font-mono text-red-400">{nonCompliantCount}</p>
          <p className="text-[10px] text-cyber-muted font-mono">{criticalOpenTasks} critical tasks open</p>
        </div>

        <div className="bg-[#0F1117] border border-cyber-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList size={14} className="text-cyber-blue" />
            <span className="text-[10px] text-cyber-muted font-mono uppercase tracking-widest">Tasks</span>
          </div>
          <p className="text-2xl font-bold font-mono text-white">{completedTasks}/{allTasks.length}</p>
          <div className="mt-1.5 h-1 bg-cyber-border rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-cyber-blue transition-all duration-500"
              style={{ width: `${allTasks.length > 0 ? (completedTasks / allTasks.length) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Section Progress Bar */}
      <div className="bg-[#0F1117] border border-cyber-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-cyber-text flex items-center gap-1.5">
            <TrendingUp size={13} className="text-cyber-blue" />
            Section Compliance Overview
          </span>
          <div className="flex items-center gap-3 text-[10px] font-mono">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /><span className="text-cyber-muted">Compliant</span></span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" /><span className="text-cyber-muted">In Review</span></span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /><span className="text-cyber-muted">Non-Compliant</span></span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-cyber-muted inline-block" /><span className="text-cyber-muted">Pending</span></span>
          </div>
        </div>
        <div className="flex gap-0.5 h-3 rounded-full overflow-hidden">
          {complianceSections.map(s => {
            const color = s.status === 'compliant' ? '#00E676' : s.status === 'non_compliant' ? '#FF3B3B' : s.status === 'in_review' ? '#FFB800' : '#3A4560';
            return (
              <div
                key={s.section}
                className="flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                style={{ backgroundColor: color }}
                title={`${s.section} ${s.title}: ${s.score}%`}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-1.5">
          {complianceSections.map(s => (
            <span key={s.section} className="text-[9px] font-mono text-cyber-muted flex-1 text-center">{s.section}</span>
          ))}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={13} className="text-cyber-muted" />
        {(['all', 'compliant', 'non_compliant', 'in_review', 'pending'] as FilterStatus[]).map(f => (
          <button
            key={f}
            onClick={() => setFilterStatus(f)}
            className={`px-3 py-1 rounded-lg text-[11px] font-mono font-medium border transition-all ${
              filterStatus === f
                ? 'bg-cyber-blue/15 border-cyber-blue/30 text-cyber-blue' :'bg-transparent border-cyber-border text-cyber-muted hover:text-cyber-text hover:border-cyber-text/20'
            }`}
          >
            {f === 'all' ? `All (${total})` : f === 'compliant' ? `Compliant (${compliantCount})` : f === 'non_compliant' ? `Non-Compliant (${nonCompliantCount})` : f === 'in_review' ? `In Review (${inReviewCount})` : `Pending (${pendingCount})`}
          </button>
        ))}
      </div>

      {/* Checklist Sections */}
      <div className="space-y-2">
        {filteredSections.map(section => {
          const config = statusConfig[section.status];
          const StatusIcon = config.icon;
          const isExpanded = expandedSection === section.section;
          const sectionTasks = section.remediationTasks;
          const doneTasks = sectionTasks.filter(t => taskStates[t.id]).length;

          return (
            <div
              key={section.section}
              className={`bg-[#0F1117] border rounded-xl overflow-hidden transition-all duration-200 ${
                isExpanded ? 'border-cyber-blue/30' : 'border-cyber-border hover:border-cyber-border/80'
              }`}
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.section)}
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
              >
                <span className="text-xs font-mono text-cyber-blue font-bold w-8 shrink-0">{section.section}</span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-white">{section.title}</span>
                    {section.score > 0 && (
                      <span className={`text-[10px] font-mono ${config.textColor}`}>{section.score}%</span>
                    )}
                  </div>
                  {section.score > 0 && (
                    <div className="mt-1.5 h-1 bg-cyber-border rounded-full overflow-hidden max-w-[200px]">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${section.score}%`, backgroundColor: config.color }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  {sectionTasks.length > 0 && (
                    <span className="text-[10px] font-mono text-cyber-muted hidden sm:block">
                      {doneTasks}/{sectionTasks.length} tasks
                    </span>
                  )}
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-mono ${config.bg} ${config.border}`} style={{ color: config.color }}>
                    <StatusIcon size={11} />
                    <span>{config.label}</span>
                  </div>
                  <span className="text-cyber-muted">
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </span>
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-cyber-border/50 px-4 pb-4 pt-3 space-y-4">
                  {/* Description */}
                  <div className="bg-[#080B12] rounded-lg p-3 border border-cyber-border/50">
                    <p className="text-[11px] text-cyber-muted font-mono leading-relaxed">{section.description}</p>
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-cyber-border/30">
                      <span className="text-[10px] font-mono text-cyber-muted">
                        Last Audit: <span className="text-cyber-text">{section.lastAudit}</span>
                      </span>
                      <span className="text-[10px] font-mono text-cyber-muted">
                        Score: <span style={{ color: config.color }}>{section.score > 0 ? `${section.score}%` : 'N/A'}</span>
                      </span>
                    </div>
                  </div>

                  {/* Remediation Tasks */}
                  {sectionTasks.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-[11px] font-semibold text-cyber-text uppercase tracking-widest font-mono flex items-center gap-1.5">
                          <ClipboardList size={11} className="text-cyber-blue" />
                          Remediation Tasks
                        </h4>
                        <span className="text-[10px] font-mono text-cyber-muted">{doneTasks}/{sectionTasks.length} complete</span>
                      </div>
                      <div className="space-y-2">
                        {sectionTasks.map(task => {
                          const pConfig = priorityConfig[task.priority];
                          const isDone = taskStates[task.id];
                          return (
                            <div
                              key={task.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                                isDone
                                  ? 'bg-green-500/5 border-green-500/15 opacity-60' :'bg-[#080B12] border-cyber-border/50 hover:border-cyber-border'
                              }`}
                            >
                              <button
                                onClick={() => toggleTask(task.id)}
                                className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                                  isDone
                                    ? 'bg-green-500 border-green-500' :'border-cyber-border hover:border-cyber-blue bg-transparent'
                                }`}
                              >
                                {isDone && <CheckCircle size={10} className="text-white" />}
                              </button>
                              <div className="flex-1 min-w-0">
                                <p className={`text-[12px] font-medium ${isDone ? 'line-through text-cyber-muted' : 'text-cyber-text'}`}>
                                  {task.task}
                                </p>
                                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                  <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${pConfig.color} ${pConfig.bg} ${pConfig.border} uppercase`}>
                                    {task.priority}
                                  </span>
                                  <span className="text-[10px] font-mono text-cyber-muted">Due: {task.dueDate}</span>
                                  <span className="text-[10px] font-mono text-cyber-muted">→ {task.assignee}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Note */}
      <div className="flex items-start gap-2 p-3 bg-cyber-blue/5 border border-cyber-blue/15 rounded-lg">
        <Shield size={13} className="text-cyber-blue mt-0.5 shrink-0" />
        <p className="text-[11px] text-cyber-muted font-mono leading-relaxed">
          Compliance data is based on the <span className="text-cyber-blue">Bangladesh Cyber Security Act 2023</span> and BTRC guidelines. 
          Scores are updated after each audit cycle. Non-compliant sections require immediate remediation action.
        </p>
      </div>
    </div>
  );
}
